import { ApiHttpError, isForbidden, isUnauthorized } from '@/shared/api/client';

/** Flatten the pages returned by a cursor-paginated endpoint into rows for a view. */
export function flattenInfiniteItems<Item>(
  data: { pages: readonly { items?: readonly Item[] }[] } | undefined,
): Item[];
export function flattenInfiniteItems<Page, Item>(
  data: { pages: readonly Page[] } | undefined,
  selectItems: (page: Page) => readonly Item[],
): Item[];
export function flattenInfiniteItems<Page, Item>(
  ...args:
    | [data: { pages: readonly { items?: readonly Item[] }[] } | undefined]
    | [data: { pages: readonly Page[] } | undefined, selectItems: (page: Page) => readonly Item[]]
): Item[] {
  if (args.length === 2) {
    const [data, selectItems] = args;
    return data?.pages.flatMap(selectItems) ?? [];
  }
  return args[0]?.pages.flatMap((page) => page.items ?? []) ?? [];
}

interface VirtualRangeInstance {
  getVirtualItems: () => readonly { index: number }[];
}

interface VirtualPageFetchState {
  isScrolling: boolean;
  itemCount: number;
  lastVisibleIndex: number | undefined;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  threshold?: number;
}

interface PositionedVirtualRangeInstance {
  getTotalSize: () => number;
  options: { scrollMargin?: number };
  scrollOffset: number | null;
  scrollRect: { height: number } | null;
}

/** Whether a vertical virtual canvas intersects its shared scroll viewport. */
export function isVirtualListInViewport(instance: PositionedVirtualRangeInstance): boolean {
  const scrollOffset = instance.scrollOffset;
  const viewportHeight = instance.scrollRect?.height;
  if (scrollOffset === null || viewportHeight === undefined) return false;

  const listStart = instance.options.scrollMargin ?? 0;
  const listEnd = listStart + instance.getTotalSize();
  const viewportEnd = scrollOffset + viewportHeight;
  return listStart <= viewportEnd && listEnd >= scrollOffset;
}

/**
 * Returns whether a virtualized infinite list reached its pagination threshold
 * because the user scrolled it. Virtual emits an initial range while it is
 * measuring the mount; that range must not drain every cursor page when the
 * first page is shorter than the viewport.
 */
export function shouldFetchNextVirtualPage(
  instance: VirtualRangeInstance,
  isScrolling: boolean,
  itemCount: number,
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  isFetchNextPageError: boolean,
  threshold = 8,
): boolean {
  return shouldFetchNextVirtualPageAtIndex({
    isScrolling,
    itemCount,
    lastVisibleIndex: instance.getVirtualItems().at(-1)?.index,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    threshold,
  });
}

/**
 * Applies the virtual pagination guard when a composite virtual view maps its
 * active row range to a branch-local item index.
 */
export function shouldFetchNextVirtualPageAtIndex({
  isScrolling,
  itemCount,
  lastVisibleIndex,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  threshold = 8,
}: VirtualPageFetchState): boolean {
  if (
    !isScrolling ||
    itemCount === 0 ||
    !hasNextPage ||
    isFetchingNextPage ||
    isFetchNextPageError
  ) {
    return false;
  }

  return lastVisibleIndex !== undefined && lastVisibleIndex >= itemCount - threshold;
}

export function isFilterRejection(error: unknown): boolean {
  return error instanceof ApiHttpError && error.status === 400;
}

/** Keep cold list failures on their owning route unless they are auth redirects or filter input. */
export function shouldEscalateToRouteBoundary(error: unknown, hasData: boolean): boolean {
  return !hasData && !isUnauthorized(error) && !isForbidden(error) && !isFilterRejection(error);
}
